import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { ContainerFormPrint, HeaderFormContainer } from './styles';
import { AButton, ATitle, Loading, ToSignature } from '../../../components';
import { LeftCircleOutlined, SignatureOutlined } from '@ant-design/icons';
import { useConfiguration } from '../../../hooks/configuration';
import api from '../../../services/api';
import { FormReport } from '../../../shared/reports/Form';
import { IReponseFormData } from '../../../shared/interfaces';
import { hasAllowedModule } from '../../../shared/utils/modules';

const FormClientPrint: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { configuration } = useConfiguration();
  const [loading, setLoading] = useState<boolean>(true);
  const [formData, setFormData] = useState<IReponseFormData>();
  const [professionalId, setProfessionalId] = useState<string | null>(null);
  const [modalSign, setModalSign] = useState<boolean>(false);
  const [validationCode, setValidationCode] = useState<string | null>(null);
  const [signatureMode, setSignatureMode] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [reportResponse, formResponse] = await Promise.all([
        api.get(`/forms-data/list/${id}/report`),
        api.get(`/clients-forms/${id}`),
      ]);
      setFormData(reportResponse.data);
      setProfessionalId(formResponse.data?.form?.professional_id || null);
      setValidationCode(
        formResponse.data?.signature_validation_code ||
          reportResponse.data?.form?.signature_validation_code ||
          null,
      );
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao encontrar prontuário',
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, []);

  if (loading) return <Loading load />;

  const isDigitallySigned = Boolean(validationCode);
  const canUseDigitalSignature = hasAllowedModule(
    configuration?.allowed_modules,
    'assinatura_digital',
  );

  return (
    <>
      <HeaderFormContainer>
        <ATitle>Imprimir Prontuário</ATitle>
        <ContainerFormPrint>
          <AButton
            type="default"
            onClick={() => navigate(-1)}
            icon={<LeftCircleOutlined />}
          >
            Voltar
          </AButton>
          {!isDigitallySigned && canUseDigitalSignature && (
            <AButton
              type="dashed"
              onClick={() => setModalSign(true)}
              icon={<SignatureOutlined />}
              style={{ backgroundColor: '#33cf5c', color: '#fff' }}
            >
              Assinar prontuário
            </AButton>
          )}
        </ContainerFormPrint>
      </HeaderFormContainer>
      {validationCode && (
        <p style={{ marginTop: 8, fontWeight: 600 }}>
          Código de validação: {validationCode}
        </p>
      )}
      {signatureMode && (
        <p style={{ marginTop: 4, fontWeight: 600 }}>
          Modo da assinatura:{' '}
          {signatureMode === 'A1' ? 'Assinado com A1' : 'Assinatura interna'}
        </p>
      )}
      {formData && (
        <FormReport
          response={formData}
          validationCode={
            validationCode || formData?.form?.signature_validation_code
          }
        />
      )}
      <ToSignature
        open={modalSign && canUseDigitalSignature}
        close={() => setModalSign(false)}
        professional_id={professionalId}
        onAuthenticated={async (_authData, password) => {
          const response = await api.post('/signature-validation/sign', {
            document_type: 'FORM',
            record_id: id,
            professional_id: professionalId,
            password,
          });

          setValidationCode(response.data.validation_code || null);
          setSignatureMode(response.data.signature_mode || null);
          addToast({
            type: 'success',
            title: 'Prontuário assinado',
            description: 'Prontuário assinado digitalmente com sucesso.',
          });
        }}
      />
    </>
  );
};
export default FormClientPrint;
